
import {
  EMPLOYEE_EMPLOYMENT_STATUS_MARKING,
  EMPLOYEE_TABS_VALUES,
  NOT_COMPLIANT_LABEL,
} from "@/constants/compliance";
import { TEmployee, TEmployee_List, TEmployee_Table } from "@/models/employees";
import { TTab } from "@/models/tabs";
import { SMApp, SMUser } from "@/store";
import { API_ROUTES } from "@/utils/API";
import { Component, Vue, Watch } from "vue-property-decorator";

const STATUS_COMPLIANT = "compliant";
const STATUS_NOT_COMPLIANT = "not_compliant";

const VALUE_NAME = "name";
const VALUE_JOB_TITLE = "jobTitle";
const VALUE_EMPLOYEE_TYPE = "employeeType";
const VALUE_IMMIGRATION_CATEGORY = "immigrationCategory";
const VALUE_VISA_EXPIRATION = "visaExpiration";
const VALUE_STATUS = "status";

const VALUE = {
  [VALUE_NAME]: VALUE_NAME,
  [VALUE_JOB_TITLE]: VALUE_JOB_TITLE,
  [VALUE_EMPLOYEE_TYPE]: VALUE_EMPLOYEE_TYPE,
  [VALUE_IMMIGRATION_CATEGORY]: VALUE_IMMIGRATION_CATEGORY,
  [VALUE_VISA_EXPIRATION]: VALUE_VISA_EXPIRATION,
  [VALUE_STATUS]: VALUE_STATUS,
};

const ICONS = {
  person: "person-outline",
};

const EMPLOYEE = "employee";

const TABLE_INITIAL_LIMIT = 50;
const TABLE_INITIAL_OFFSET = 0;
const TABLE_INITIAL_ORDER = "asc";
const TABLE_INITIAL_ORDER_BY = "id";

type ParamsKeyObject = {
  [key: string]: any;
};

@Component({
  components: {
    ComplianceExpiredWidget: () => import("@/modules/Compliance/ComplianceExpiredWidget.vue"),
    CompliancePlaceholder: () => import("@/components/Placeholder/CompliancePlaceholder.vue"),
    TableSearchbar: () => import("@/components/TableSearchbar/TableSearchbar.vue"),
    Tabs: () => import("@/components/Tabs/Tabs.vue"),
  },
})
export default class Employees extends Vue {
  params: ParamsKeyObject = {
    limit: TABLE_INITIAL_LIMIT,
    offset: TABLE_INITIAL_OFFSET,
    order: TABLE_INITIAL_ORDER,
    orderBy: TABLE_INITIAL_ORDER_BY,
    search: "",
  };

  value = VALUE;

  icons = ICONS;

  error = this.$root.$tc("caption.delete");

  loading = false;

  onboardEmployeeLoading = false;

  employeesData: TEmployee_Table[] = [];

  employee = EMPLOYEE;

  get companyId(): number {
    return Number(this.$route.params.company_id ?? 0);
  }

  get endpoint() {
    if (this.companyId) return API_ROUTES.EXPERT_COMPANIES_EMPLOYEES(this.companyId);

    return API_ROUTES.COMPANY_EMPLOYEES;
  }

  mounted() {
    this.fetchEmployees();
  }

  hasEmployees = false;

  async fetchEmployees() {
    try {
      this.loading = true;

      const params = {
        search: this.params?.search || "",
        list_name: this.currentTabName,
      };

      const { data } = await this.axios.get<TEmployee>(this.endpoint, {
        params,
      });

      if (!data) return;

      this.employeesData = data.employee_list.map((a) => this.convertEmployeesForTable(a));

      this.employeesData = this.sortEmployeesData(this.employeesData);

      this.tabDigits.active = data.count.active_count;
      this.tabDigits.leavers = data.count.terminated_count;

      if (!this.params?.search) {
        this.hasEmployees = !!this.employeesData.length;
      }
    } finally {
      this.loading = false;
    }
  }

  sortEmployeesData(items) {
    const itemsGroupedByStatus = items.reduce((acc, cur) => {
      if (!Object.prototype.hasOwnProperty.call(acc, cur.status)) {
        Object.assign(acc, {
          [cur.status]: [],
        });
      }

      acc[cur.status].push(cur);

      return acc;
    }, {});

    const prependGroup = [];
    const appendGroup = [];

    Object.keys(itemsGroupedByStatus).forEach((status) => {
      if (status === NOT_COMPLIANT_LABEL) {
        prependGroup.push(...itemsGroupedByStatus[status]);
      } else {
        appendGroup.push(...itemsGroupedByStatus[status]);
      }
    });

    prependGroup.sort((a, b) => (a.name > b.name ? 1 : -1));
    appendGroup.sort((a, b) => (a.name > b.name ? 1 : -1));

    return [...prependGroup, ...appendGroup];
  }

  headers = [
    {
      text: this.$t("table.headers.name"),
      value: this.value.name,
      slot: true,
      sortable: true,
      sortBy: "text",
    },
    {
      text: this.$t("table.headers.jobTitle"),
      value: this.value.jobTitle,
      filterable: true,
    },
    {
      text: this.$t("table.headers.employeeType"),
      value: this.value.employeeType,
      filterable: true,
    },
    {
      text: this.$t("table.headers.immigrationCategory"),
      value: this.value.immigrationCategory,
      filterable: true,
    },
    {
      text: this.$t("table.headers.visaExpiration"),
      value: this.value.visaExpiration,
      sortable: true,
      sortBy: "date",
    },
    {
      text: this.$t("table.headers.status"),
      value: this.value.status,
      error: this.$root.$tc("labels.notCompliant"),
      filterable: true,
    },
  ];

  get isComplianceBlocked() {
    return SMUser.user?.is_compliance_blocked;
  }

  get widgetExpiredDescription() {
    return SMUser.isEmployer
      ? this.$tc("widget.expired.description.employer")
      : this.$tc("widget.expired.description.employee");
  }

  get labels() {
    return {
      allLabel: this.$t("table.allLabel"),
      placeholderLabel: this.$t("table.placeholderLabel"),
    };
  }

  get attributes() {
    const { headers, employeesData, loading, labels } = this;

    return {
      headers,
      items: employeesData,
      loading,
      labels,
    };
  }

  get placeholderDescription() {
    if (SMUser.isSponsor && !SMUser.isSponsorConfirmation)
      return this.$tc("placeholder.descriptionSLNoSLNumber");

    return this.$tc("placeholder.description");
  }

  async onboardEmployee() {
    try {
      this.onboardEmployeeLoading = true;

      const { data } = await this.axios.get(API_ROUTES.COMPANY_EMPLOYEE_ONBOARD_EMPLOYEE);

      if (!data) return;

      const { task_id } = data;

      this.$router.push(SMUser.basePath + `/tasks/${task_id}`);
    } finally {
      this.onboardEmployeeLoading = false;
    }
  }

  convertStatus(status: string): string {
    if (status === STATUS_COMPLIANT) return `${this.$root.$tc("labels.compliant")}`;

    if (status === STATUS_NOT_COMPLIANT) return `${this.$root.$tc("labels.notCompliant")}`;

    return "―";
  }

  convertEmployeesForTable(a: TEmployee_List): TEmployee_Table {
    return {
      id: a.id,
      name: a.first_name && a.last_name ? `${a.first_name} ${a.last_name}` : "―",
      jobTitle: a.job_title ? a.job_title : "―",
      employeeType: a.type ? a.type : "―",
      immigrationCategory: a.immigration_category ? a.immigration_category : "―",
      visaExpiration: a.visa_expiration ? a.visa_expiration : "―",
      status: this.convertStatus(a.status),
      userId: a.user_id,
      workingStatus: a.working_status,
      job_title: "",
      badgeType: "",
      job_position: "",
      email: a.email,
    };
  }

  setPageSectionName(item: TEmployee_Table) {
    const { name } = item;

    if (!name) return;

    SMApp.setEmployeeName(name);

    const sectionName = ` > ${name}`;

    SMApp.setPageSection(sectionName);
  }

  navigateEmployee(item: TEmployee_Table) {
    if (!item.userId) return;

    const { userId } = item;

    const path = this.isExpert
      ? `${SMUser.basePath}/companies/${this.companyId}/employees/${userId}`
      : `${SMUser.basePath}/employees/${userId}`;

    const routeData = this.$router.resolve(path);

    window.open(routeData.href, "_blank");
  }

  get isEmployer() {
    return SMUser.isEmployer;
  }

  get isExpert() {
    return SMUser.isExpert;
  }

  get isSponsorConfirmation() {
    if (this.isExpert) return true;

    return SMUser.isSponsorConfirmation;
  }

  tabNames: string[] = EMPLOYEE_TABS_VALUES;

  currentTabName = this.tabNames[0];

  componentKey = 0;

  tabDigits = {
    active: 0,
    leavers: 0,
  };

  get tabs(): TTab[] {
    return this.tabNames.map((name) => ({
      label: name.toUpperCase(),
      isActive: this.currentTabName === name,
      digit: this.tabDigits[name],
      action: () => {
        this.currentTabName = name;
        this.componentKey += 1;
      },
    }));
  }

  init = false;

  @Watch("currentTabName", { immediate: true })
  async currentTabNameChanged() {
    try {
      await this.fetchEmployees();
    } finally {
      this.init = true;
    }
  }

  getClass(workingStatus: string) {
    return EMPLOYEE_EMPLOYMENT_STATUS_MARKING[workingStatus] || "";
  }

  get isTabNameActive() {
    return (this.currentTabName = this.tabNames[0]);
  }
}
